import { useState, useEffect } from "react";
import { ShoppingBag, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { BACKEND_URL } from "../context/AuthContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";

const Shop = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/shop/products`)
      .then((r) => r.json())
      .then((data) => setProducts(Array.isArray(data) ? data : []))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <SEO title="Merchandise Shop" description="Official DJsandMCMedia™ merchandise. Caps, hoodies, t-shirts, and custom branded gear for DJs, MCs, and Podcasters." />
      <Header />

      <main className="max-w-6xl mx-auto px-6 py-20">
        <div className="text-center mb-14">
          <img src="/logo-header.png" alt="DJs&MC Media" className="h-28 w-auto mx-auto mb-6" />
          <h1 data-testid="shop-heading" className="text-4xl sm:text-5xl font-bold mb-3">Official Merch</h1>
          <p className="text-white/50 text-base max-w-lg mx-auto">
            Rep the culture. Premium quality merch for DJs, MCs, and music lovers.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-4 border-white/20 border-t-purple-500 rounded-full animate-spin" />
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-20">
            <ShoppingBag className="w-16 h-16 text-white/20 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white/60 mb-2">Coming Soon</h2>
            <p className="text-white/40 text-sm">New merch is on the way. Check back soon!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16" data-testid="shop-grid">
            {products.map((product) => (
              <div
                key={product.product_id}
                data-testid={`shop-product-${product.product_id}`}
                className="group bg-white/[0.03] border border-white/[0.08] rounded-2xl overflow-hidden hover:border-purple-500/30 transition-all"
              >
                <div className="relative aspect-square bg-black/40 overflow-hidden">
                  {product.image && (
                    <img
                      src={product.image.startsWith("http") ? product.image : product.image}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  )}
                  {product.tag && (
                    <span className="absolute top-3 left-3 bg-purple-600 text-white text-[11px] font-semibold px-2.5 py-1 rounded-full">
                      {product.tag}
                    </span>
                  )}
                </div>
                <div className="p-5">
                  <h3 className="text-white font-semibold mb-1">{product.name}</h3>
                  <p className="text-white/40 text-xs leading-relaxed mb-3">{product.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xl font-bold text-white">${product.price}</span>
                    {product.buy_link ? (
                      <a
                        href={product.buy_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`shop-buy-${product.product_id}`}
                        className="inline-flex items-center gap-1.5 bg-purple-600 hover:bg-purple-500 text-white text-sm px-4 py-2 rounded-full transition font-medium"
                      >
                        Buy Now <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    ) : (
                      <span className="text-white/30 text-sm">Coming Soon</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="rounded-2xl p-8 text-center" style={{ background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)", border: "1px solid rgba(139,92,246,0.15)" }}>
          <ShoppingBag className="w-10 h-10 text-purple-400 mx-auto mb-3" />
          <h2 className="text-xl font-bold mb-2">Want custom merch for your channel?</h2>
          <p className="text-white/50 text-sm mb-4 max-w-md mx-auto">
            Premium plan members can get custom branded merchandise with their own station logo. Contact us for details.
          </p>
          <Link to="/#contact" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/15 text-white px-6 py-2.5 rounded-full text-sm font-medium transition">
            Contact Us
          </Link>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Shop;
